"use client";

import { Sparkles, Gamepad2, BarChart3, BookOpen } from "lucide-react";
import { type ReactNode } from "react";
import FeatureCard from "./FeatureCard";
import { useI18n } from "@/lib/i18n/LanguageProvider";
import { useTrackSection } from "@/hooks/useTrackSection";

// Language-independent media config. Text (title, details, tab labels) comes
// from the dictionary and is merged by index in the component below.
interface FeatureMedia {
  icon: ReactNode;
  /** When true, show the "Try free" link (URL is locale-aware). */
  hasLink?: boolean;
  /** Keys + video URLs for tabbed media; tab labels are localized. */
  videoTabs?: { key: string; videoUrl: string }[];
  singleVideoUrl?: string;
  multipleImageUrls?: string[];
}

const featureMedia: FeatureMedia[] = [
  {
    icon: <Sparkles className="h-6 w-6 text-primary" />,
    hasLink: true,
    videoTabs: [
      { key: "create-program", videoUrl: "https://cl.teado.ai/video/create-programs.mp4" },
      { key: "create-exercise", videoUrl: "https://cl.teado.ai/video/create-test.mp4" },
      { key: "ai-grading", videoUrl: "https://cl.teado.ai/video/teado-ai-grading.mp4" },
    ],
  },
  {
    icon: <Gamepad2 className="h-6 w-6 text-primary" />,
    singleVideoUrl: "https://cl.teado.ai/video/giao-dien-game-hoa.mp4",
  },
  {
    icon: <BarChart3 className="h-6 w-6 text-primary" />,
    hasLink: true,
    videoTabs: [
      { key: "assign-homework", videoUrl: "https://cl.teado.ai/video/assign-homework.mp4" },
      { key: "placement-test", videoUrl: "https://cl.teado.ai/video/placement-test.mp4" },
      { key: "track-progress", videoUrl: "https://cl.teado.ai/video/track-student-progress.mp4" },
    ],
  },
  {
    icon: <BookOpen className="h-6 w-6 text-primary" />,
    multipleImageUrls: [
      "/assets/certifications/eaf-certified-2024.png",
      "/assets/certifications/iceie-bronze-2024.png",
      "/assets/certifications/edtech-vietnam-2023.png",
    ],
  },
];

const FeaturesSection = () => {
  const { t } = useI18n();
  const sectionRef = useTrackSection<HTMLElement>("features");

  return (
    <section
      ref={sectionRef}
      id="features"
      className="py-16 md:py-24 bg-gradient-to-b from-muted/30 to-background"
    >
      <div className="container mx-auto px-4">
        <h2 className="font-bold text-center mb-16 text-foreground py-5 leading-relaxed">
          <span className="md:text-4xl block text-[1.375rem] text-balance px-2">
            {t.features.titleLine1}
            {t.features.titleLine2 && (
              <>
                <br />
                {t.features.titleLine2}
              </>
            )}
          </span>
          <span className="gradient-text md:text-4xl text-[1.375rem]">
            {t.features.titleHighlight}
          </span>
        </h2>

        <div className="space-y-24">
          {featureMedia.map((media, i) => {
            const item = t.features.items[i];
            const videoTabs = media.videoTabs?.map((tab, j) => ({
              ...tab,
              label: item.tabLabels?.[j] ?? tab.key,
            }));

            return (
              <FeatureCard
                key={i}
                icon={media.icon}
                title={item.title}
                description=""
                details={item.details}
                link={media.hasLink ? t.links.teacherFeature : undefined}
                linkText={media.hasLink ? t.features.tryFree : undefined}
                singleVideoUrl={media.singleVideoUrl}
                multipleImageUrls={media.multipleImageUrls}
                videoTabs={videoTabs}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
